import { Modal as AntdModal, Col, Row, Button as AntdButton, Checkbox } from "antd"
import { CheckboxChangeEvent } from "antd/lib/checkbox"
import { ArrowLeftOutlined } from "@ant-design/icons"
import { formatPhoneNumber } from "react-phone-number-input"
import DOMPurify from "dompurify"
import { omit } from "lodash"
import { useEffect, useState } from "react"
import config from "react-global-configuration"
import { useDispatch } from "react-redux"

import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons } from "components/Card/Card"
import PreviousButton from "components/Previous/PreviousButton"
import { Title } from "components/Title/Title"
import { logout } from "containers/Login/services/actions"
import { cdn } from "core/cdn"
import { languages } from "lib/languages"
import { ModalProps } from "types/props"

import styles from "./Modal.module.scss"

export const Modal: React.FC<ModalProps> = (props): JSX.Element => {
  const ErrorIcon = () => (
    <img
      src={cdn("images/devices/tutorial_device/attention.png")}
      alt="error icon"
    />
  )

  const titleHeader = (
    <Row justify="center" wrap={false} style={{ marginTop: "50px" }}>
      {props.iconError && (
        <Col flex="none">
          <ErrorIcon />
        </Col>
      )}
      <Col flex="auto">
        <Title text={props.title} {...props.titleProps} />
      </Col>
      {props.iconError && (
        <Col flex="none">
          <ErrorIcon />
        </Col>
      )}
    </Row>
  )

  const title = props.closable ? (
    <div>
      <PreviousButton
        text={languages.close}
        onClick={() => {
          if (props.onClose) props.onClose()
          else if (props.onCancel) props.onCancel()
        }}
      />
      {props.title ? titleHeader : null}
    </div>
  ) : props.title ? (
    titleHeader
  ) : null

  return (
    <AntdModal
      width={props.width ?? "100%"}
      className={styles.Modal}
      centered
      title={title}
      bodyStyle={props.bodyStyle}
      closable={false}
      open={props.visible}
      footer={
        props.footer ? (
          props.footer
        ) : !props.noFooter ? (
          <Buttons>
            {props.onCancel && (
              <Button
                wide="long"
                type="link"
                onClick={props.onCancel}
                {...props.cancelButtonProps}
              >
                {props.cancelText || languages.previous}
              </Button>
            )}
            {props.onAccept && (
              <Button
                wide="long"
                loading={props.loading}
                disabled={props.loading}
                onClick={props.onAccept}
                {...props.acceptButtonProps}
              >
                {props.acceptText || languages.confirm}
              </Button>
            )}
          </Buttons>
        ) : null
      }
    >
      {props.children}
    </AntdModal>
  )
}

export const GenericModal: React.FC<ModalProps & { EmailDesign?: boolean }> = (props): JSX.Element => (
  <Modal
    {...props}
    footer={
      props.footer ? (
        props.footer
      ) : !props.noFooter ? (
        <Buttons style={{ ...props.buttonWrapperStyle, ...(props.EmailDesign && { display: "flex", flexDirection: 'column', gap: "5px" }) }}>
          {props.onCancel && (
            <Button
              wide="long"
              {...props.cancelButtonProps}
              onClick={() => props.onCancel && props.onCancel()}
              {...(props.EmailDesign && { type: 'primary' })}
            >
              {props.cancelText || languages.cancel}
            </Button>
          )}
          {props.onAccept && (
            <Button
              wide="long"
              danger
              type="primary"
              {...(props.EmailDesign && { type: 'link', style: { textTransform: "none" } })}
              {...props.acceptButtonProps}
              onClick={() => props.onAccept && props.onAccept()}
            >
              {props.EmailDesign ? <span style={{ textDecoration: "underline" }}>{props.acceptText}</span> : props.acceptText}
            </Button>
          )}
        </Buttons>
      ) : undefined
    }
  >
    <div className="card-contenant font-size-large">
      <div
        dangerouslySetInnerHTML={{
          __html:
            DOMPurify.sanitize(props.contentText, {
              USE_PROFILES: { html: true },
            }) || "",
        }}
      ></div>
      {props.children}
    </div>
  </Modal>
)

export const ConsentModal = (props: {
  visible: boolean
  onAccept: (newsletterSubscription: boolean) => void
  onCancel: () => void
  hasValidEmail?: boolean
}): JSX.Element => {
  const [showCGU, setShowCGU] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [newsletterSubscription, setNewsletterSubscription] = useState(false)
  const [cguAccepted, setCguAccepted] = useState(false)
  const canOfferNewsletter = props.hasValidEmail !== false
  const showAcceptButton = !showCGU && !showPrivacy
  const canAccept = !canOfferNewsletter || cguAccepted
  const onAccept = showAcceptButton
    ? () => props.onAccept(canOfferNewsletter && newsletterSubscription)
    : undefined
  const cguConsentContent = (
    <>
      {languages.cguConsentPrefix}{" "}
      <a onClick={(e) => { e.stopPropagation(); setShowCGU(true) }}>
        {languages.cguConditionsLabel}
      </a>{" "}
      {languages.cguConsentAnd}{" "}
      <a
        onClick={(e) => {
          e.stopPropagation()
          setShowPrivacy(true)
        }}
      >
        {" "}
        {languages.privacyPolicyLabel}
      </a>{" "}
      {languages.cguConsentMedadomSuffix}{" "}
    </>
  )
  return (
    <GenericModal
      {...props}
      onAccept={onAccept}
      title={languages.cguAndPrivacyTitle}
      acceptText={canOfferNewsletter ? languages.continue : languages.IAcceptConsent}
      acceptButtonProps={{ danger: false, disabled: showAcceptButton && !canAccept }}
      cancelText={
        showCGU || showPrivacy ? languages.previous : languages.IRefuseConsent
      }
      onCancel={
        showCGU || showPrivacy
          ? () => {
            setShowCGU(false)
            setShowPrivacy(false)
          }
          : props.onCancel
      }
    >
      <div>
        <div
          style={
            canOfferNewsletter
              ? { textAlign: "left", display: "flex", alignItems: "flex-start", gap: 14 }
              : { textAlign: "center" }
          }
          hidden={showCGU || showPrivacy}
        >
          {canOfferNewsletter && (
            <Checkbox
              id="cgu-consent"
              checked={cguAccepted}
              onChange={(event: CheckboxChangeEvent) => {
                setCguAccepted(event.target.checked)
              }}
              style={{ marginTop: -4 }}
            />
          )}
          {canOfferNewsletter ? (
            <label htmlFor="cgu-consent">{cguConsentContent}</label>
          ) : (
            cguConsentContent
          )}
        </div>
        {canOfferNewsletter && !showCGU && !showPrivacy && (
          <div
            style={{ marginTop: 20, textAlign: "left", display: "flex", alignItems: "flex-start", gap: 14 }}
          >
            <Checkbox
              id="newsletter-subscription"
              checked={newsletterSubscription}
              onChange={(event: CheckboxChangeEvent) => {
                setNewsletterSubscription(event.target.checked)
              }}
              style={{ marginTop: -4 }}
            />
            <label htmlFor="newsletter-subscription">
              {languages.newsletterSubscriptionConsent}
            </label>
          </div>
        )}
        {
          showCGU &&
          <div
            className={styles.CGUContent}
          ><iframe src="https://info.medadom.com/cgu" style={{ width: "-webkit-fill-available", height: "-webkit-fill-available" }} /></div>
        }
        {
          showPrivacy &&
          <div className={styles.CGUContent}>
            <iframe src="https://info.medadom.com/politique-de-confidentialite" style={{ width: "-webkit-fill-available", height: "-webkit-fill-available" }} />
          </div>
        }
      </div>
    </GenericModal>
  )
}

export const IdleModal = (props: any) => {
  const Initialseconds = config.get("disconnectTimer.seconds") || 30
  const InitialMinutes = config.get("disconnectTimer.minutes") || 1

  const dispatch = useDispatch()

  const [remainingSecond, setRemainingSecond] = useState(Initialseconds)
  const [remainingMinute, setRemainingMinute] = useState(InitialMinutes)
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
  const [tabVisible, setTabVisible] = useState(true)
  useEffect(() => {
    document.addEventListener("visibilitychange", function () {
      // clear Interval if user leave the tab
      if (document.hidden) {
        timer && clearInterval(timer)
        setTabVisible(false)
      } else {
        setTabVisible(true)
      }
    })
  }, [])

  useEffect(() => {
    clearInterval(timer)
    setRemainingMinute(InitialMinutes)
    setRemainingSecond(Initialseconds)
  }, [props.isIdle])

  useEffect(() => {
    let sec = remainingSecond
    let min = remainingMinute
    if (props.visible) {
      const timer = setInterval(() => {
        sec = sec - 1 >= 0 ? sec - 1 : 59
        min = sec !== 59 ? min : min - 1
        if (!sec && !min) {
          timer && clearInterval(timer)
          props.onAccept()
          dispatch(logout())
        } else if (tabVisible) {
          setRemainingMinute(min)
          setRemainingSecond(sec)
        }
      }, 1000)
      setTimer(timer)
    } else {
      timer && clearInterval(timer)
    }
  }, [props.visible])

  return (
    <GenericModal
      {...props}
      title={languages.inactivity}
      acceptText={languages.stayLogged}
    >
      <div>{`${remainingMinute} m ${remainingSecond} ...`}</div>
    </GenericModal>
  )
}

const MEDICAL_RIGHTS = [
  {
    content: (
      <span className="center">
        <b>{languages.c2s}</b>
        <br />
        (CMU)
      </span>
    ),
    value: "C2S",
  },
  {
    content: (
      <span className="center">
        <b>{languages.ame}</b>
        <br />
        (AME)
      </span>
    ),
    value: "AME",
  },
  {
    content: (
      <span className="center">
        <b>{languages.ald}</b>
        <br />
        (ALD)
      </span>
    ),
    value: "ALD",
  },
]
const rightsStepsForRelative = [
  {
    step: "check",
    title: languages.indicateRelativeRights,
  },
  {
    step: "attest",
    title: languages.fraudIsPunished,
    contentText: languages.ameModalContent,
    acceptText: languages.ameModalAccept,
    cancelText: languages.ameModalCancelRelative,
  },
]
const rightsSteps = [
  {
    step: "check",
    title: languages.indicateYourRights,
  },
  {
    step: "attest",
    title: languages.fraudIsPunished,
    contentText: languages.ameModalContent,
    acceptText: languages.ameModalAccept,
    cancelText: languages.ameModalCancel,
  },
]

const RightsCheckboxes = (props: {
  onCancel?: () => void
  onAccept: (value: string) => void
  relative?: boolean
}) => {
  const [right, setRight] = useState<string>()

  return (
    <>
      <Buttons style={{ paddingTop: "0" }}>
        {MEDICAL_RIGHTS.map((mRight, key) => (
          <FatButton
            key={key}
            clicked={mRight.value === right}
            onClick={() => setRight(mRight.value)}
          >
            {mRight.content}
          </FatButton>
        ))}
      </Buttons>
      <Buttons style={{ paddingTop: "100px" }}>
        <Button wide="long" type="primary" onClick={props.onCancel}>
          {languages.cancel}
        </Button>
        <Button
          disabled={!right}
          wide="long"
          onClick={() => right && props.onAccept(right)}
        >
          {languages.next}
        </Button>
      </Buttons>
    </>
  )
}

export const RightsModal: React.FC<ModalProps & { relative?: boolean }> = (
  props
): JSX.Element => {
  const [step, setStep] = useState<"check" | "attest">("check")
  const content = props.relative
    ? (rightsStepsForRelative.find((s) => step === s.step) as any)
    : (rightsSteps.find((s) => step === s.step) as any)

  return (
    <GenericModal
      {...omit(props, "relative")}
      title={content.title}
      contentText={content.contentText}
      acceptText={content.acceptText}
      cancelText={content.cancelText}
      acceptButtonProps={{ wide: "default", type: "default", danger: false }}
      cancelButtonProps={{ wide: "default", type: "primary", danger: false }}
      noFooter={step === "check"}
      onCancel={() => {
        setStep("check")
        props.onCancel && props.onCancel()
      }}
    >
      {step === "check" ? (
        <RightsCheckboxes
          onCancel={props.onCancel}
          onAccept={() => setStep("attest")}
          relative={props.relative}
        />
      ) : undefined}
    </GenericModal>
  )
}

const PreviousCloseIcon = (): JSX.Element => (
  <div style={{ padding: '15px' }}><AntdButton type="default" style={{ borderRadius: '0px', fontSize: '18px', lineHeight: 1, height: '60px', textTransform: 'uppercase' }}> <ArrowLeftOutlined /> {languages.previous}</AntdButton></div>
)

export const ExistingPhoneModal = ({
  visible,
  onAccept,
  onClose,
  phoneNumber,
}: {
  visible: boolean
  onAccept: () => void
  onClose: () => void
  phoneNumber: string
}): JSX.Element => (
  <AntdModal
    width="70%"
    className={styles.ExistingPhoneModal}
    centered
    title={<Title text={<img src={cdn('/images/warning.svg')} alt="warning" />} />}
    closable={true}
    closeIcon={<PreviousCloseIcon />}
    onCancel={onClose}
    open={visible}
    footer={false}
  >
    <div className={styles.ExistingPhoneModalContent}>
      <div>{languages.phoneAlreadyExist}</div>
      <div className={styles.ExistingPhoneInfo}>{formatPhoneNumber(phoneNumber)}</div>
    </div>
    <Buttons style={{ marginTop: '20px' }} direction="vertical">
      <Button wide="long" style={{ fontSize: '28px', fontWeight: 'bold' }} type='primary' onClick={onAccept}>{languages.logMeIn}</Button>
      <Button wide="long" type="link" style={{ fontSize: '28px' }} onClick={onClose}><u>{languages.modifyPhoneNumber}</u></Button>
    </Buttons>
  </AntdModal>
)

export const CancelSignupModal = ({
  visible,
  onCancelSignup,
  onModifyPhone,
  onClose,
}: {
  visible: boolean
  onCancelSignup: () => void
  onModifyPhone: () => void
  onClose: () => void
}): JSX.Element => (
  <AntdModal
    width="70%"
    className={styles.ExistingPhoneModal}
    centered
    title={<Title text={<img src={cdn('/images/warning.svg')} alt="warning" />} />}
    closable={true}
    closeIcon={<PreviousCloseIcon />}
    onCancel={onClose}
    open={visible}
    footer={false}
  >
    <div className={styles.ExistingPhoneModalContent} style={{ fontSize: '48px', lineHeight: 1.3 }}>
      {languages.cancelSignupQuestion_1}
      <b>{languages.cancelSignupQuestion_bold1}</b>
      <br />
      {languages.cancelSignupQuestion_2}
      <b>{languages.cancelSignupQuestion_bold2}</b>
      {languages.cancelSignupQuestion_3}
    </div>
    <Buttons style={{ paddingTop: '50px' }} size={40}>
      <Button style={{ width: '560px', height: '110px', fontSize: '38px', color: '#211452', borderColor: '#211452' }} type="default" onClick={onModifyPhone}>{languages.modifyMyNumber}</Button>
      <Button style={{ width: '560px', height: '110px', fontSize: '38px' }} type="primary" danger onClick={onCancelSignup}>{languages.cancelMySignup}</Button>
    </Buttons>
  </AntdModal>
)

export const EngineDiagnoticModal: React.FC<{
  onAccept: any
  onCancel: any
  visible: boolean
  children: JSX.Element
  title?: string | JSX.Element
}> = (props): JSX.Element => (
  <AntdModal
    width="70%"
    className={styles.EngineDiagnoticModal}
    centered
    title={<Title text={props.title} />}
    closable={false}
    open={props.visible}
    footer={false}
  >
    <div>{props.children}</div>

    <Buttons direction="vertical">
      <Button
        wide="long"
        type="primary"
        onClick={props.onCancel}
        style={{ fontSize: "28px", maxHeight: "80px" }}
      >
        {languages.restartDiagnostic.toUpperCase()}
      </Button>
      <Button
        wide="long"
        danger
        type="link"
        style={{ fontWeight: "bold", fontSize: "28px", maxHeight: "80px" }}
        onClick={props.onAccept}
      >
        {languages.continueAnyway.toUpperCase()}
      </Button>
    </Buttons>
  </AntdModal>
)
